import React, { useEffect, useMemo, useState } from "react";

import dayjs from "dayjs";
import { CheckCircle2, Loader2, Search } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { EmptyState } from "~/components/ui/empty-state";
import { Input } from "~/components/ui/input";
import { useToast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import { cn } from "~/lib/utils";
import { formatCurrency, formatDate, formatRut } from "~/utils/helpers";

interface AssociateFacturaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // The orphaned BSale factura to link (subset of the row shape).
  factura: {
    id: number;
    number: number;
    totalAmount: number;
    emissionDate: number;
  } | null;
  onAssociated: () => void;
}

interface CandidateSalesRequest {
  id: string;
  price_with_iva: number | null;
  total_amount: number | null;
  status: string | null;
  created_at: string;
  client: { name: string | null; tax_id: string | null } | null;
}

// A factura's BSale total matches a candidate either at gross (afecta) or at net
// (exenta SII 34 has no IVA). Compare against both so exempt facturas still match.
const totalMatchesCandidate = (
  candidate: { price_with_iva: number | null; total_amount: number | null },
  facturaTotal: number
): boolean =>
  Number(candidate.price_with_iva) === Number(facturaTotal) ||
  Number(candidate.total_amount) === Number(facturaTotal);

// Links an already-emitted (orphaned) factura to a "Por Facturar" sales note.
// The picker lists candidates and flags total matches, but the strict client +
// total validation is enforced server-side in create-factura's adopt mode.
export function AssociateFacturaDialog({
  open,
  onOpenChange,
  factura,
  onAssociated,
}: AssociateFacturaDialogProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [candidates, setCandidates] = useState<CandidateSalesRequest[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !factura) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setSelectedId(null);
      setSearch("");

      const { data, error } = await supabase
        .from("sales_request")
        .select(
          "id, price_with_iva, total_amount, status, created_at, client:clients(name, tax_id)"
        )
        .eq("status", "por_facturar")
        .order("created_at", { ascending: false })
        .limit(100);

      if (cancelled) return;

      if (error) {
        console.error("Error loading sales requests:", error);
        toast({
          title: "Error",
          description:
            "No se pudieron cargar las notas de venta por facturar.",
          variant: "destructive",
        });
        setCandidates([]);
      } else {
        const rows: CandidateSalesRequest[] = (data ?? []).map((r: any) => ({
          id: r.id,
          price_with_iva: r.price_with_iva,
          total_amount: r.total_amount,
          status: r.status,
          created_at: r.created_at,
          // A to-one embed is an object, but normalize in case it arrives as an array.
          client: Array.isArray(r.client) ? r.client[0] ?? null : r.client ?? null,
        }));
        setCandidates(rows);
        // Pre-select when exactly one candidate matches the factura total.
        const exact = rows.filter((r) =>
          totalMatchesCandidate(r, Number(factura.totalAmount))
        );
        if (exact.length === 1) setSelectedId(exact[0].id);
      }
      setLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, factura, toast]);

  const filtered = useMemo(() => {
    const withMatch = candidates.map((c) => ({
      ...c,
      matchesTotal: factura
        ? totalMatchesCandidate(c, Number(factura.totalAmount))
        : false,
    }));
    const q = search.trim().toLowerCase();
    const result = q
      ? withMatch.filter(
          (c) =>
            (c.client?.name ?? "").toLowerCase().includes(q) ||
            (c.client?.tax_id ?? "").toLowerCase().includes(q) ||
            c.id.toLowerCase().includes(q)
        )
      : withMatch;
    // Total matches first, otherwise keep the recency order from the query.
    return [...result].sort(
      (a, b) => Number(b.matchesTotal) - Number(a.matchesTotal)
    );
  }, [candidates, search, factura]);

  const handleConfirm = async () => {
    if (!factura || !selectedId) return;
    setSubmitting(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token || "";
      // Prefer the user's token so the manual link is attributed (uploaded_by);
      // fall back to the publishable key, which also satisfies verify_jwt.
      const authHeader = accessToken || import.meta.env.VITE_SUPABASE_KEY;

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL || "https://mdrvhekhimhcwydrpueo.supabase.co"}/functions/v1/create-factura`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authHeader}`,
          },
          body: JSON.stringify({
            salesRequestId: selectedId,
            adoptBsaleId: factura.id,
            adoptBsaleNumber: factura.number,
          }),
        }
      );

      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        toast({
          title: "No se pudo asociar",
          description:
            result.error ||
            "Ocurrió un error al asociar la factura a la nota de venta.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Factura asociada",
        description: `La factura #${factura.number} se asoció correctamente a la nota de venta.`,
      });
      onOpenChange(false);
      onAssociated();
    } catch (err) {
      console.error("Error associating factura:", err);
      toast({
        title: "Error",
        description: "Error inesperado al asociar la factura.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Asociar factura a nota de venta</DialogTitle>
          <DialogDescription>
            {factura
              ? `Factura #${factura.number} · ${dayjs.unix(factura.emissionDate).format("DD-MM-YYYY")} · $${formatCurrency(factura.totalAmount)}. Seleccione la nota de venta correspondiente; se validará que el cliente y el total coincidan.`
              : null}
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por cliente o RUT…"
            className="h-9 pl-9"
          />
        </div>

        <div className="max-h-72 space-y-2 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Cargando notas de venta…
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState
              title="Sin notas de venta por facturar"
              description="No hay notas de venta en estado «Por Facturar» que coincidan. Verifique el documento o el estado de la nota de venta."
            />
          ) : (
            filtered.map((sr) => {
              const selected = sr.id === selectedId;
              return (
                <button
                  key={sr.id}
                  type="button"
                  onClick={() => setSelectedId(sr.id)}
                  className={cn(
                    "flex w-full items-center justify-between rounded-lg border p-3 text-left transition-colors",
                    selected
                      ? "border-primary bg-primary/5"
                      : "border-border hover:bg-muted/40"
                  )}
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">
                      {sr.client?.name || "Cliente sin nombre"}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {sr.client?.tax_id ? formatRut(sr.client.tax_id) : "—"} ·{" "}
                      {formatDate(sr.created_at)}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2 pl-3">
                    <span className="text-sm font-medium">
                      ${formatCurrency(Number(sr.price_with_iva) || 0)}
                    </span>
                    {sr.matchesTotal && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
                        <CheckCircle2 className="h-3 w-3" />
                        Coincide
                      </span>
                    )}
                    {selected && (
                      <CheckCircle2 className="h-4 w-4 text-primary" />
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancelar
          </Button>
          <Button onClick={handleConfirm} disabled={!selectedId || submitting}>
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Asociar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
